/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import AppLoader from "components/AppLoader";

// @mui icons
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// API Configuration
import appSettings from "appsettings.json";

function Basic() {
  const recaptchaSiteKey = appSettings?.security?.recaptchaSiteKey || "";
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [phoneNo, setPhoneNo] = useState("");
  const [otpStep, setOtpStep] = useState(false);
  const [recaptchaOpen, setRecaptchaOpen] = useState(false);
  const [pendingLoginRequest, setPendingLoginRequest] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  useEffect(() => {
    if (!recaptchaOpen || !recaptchaSiteKey) return undefined;

    const existingScript = document.querySelector('script[src*="google.com/recaptcha/api.js"]');
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    const widgetId = "admin-signin-recaptcha";
    const renderCaptcha = () => {
      const container = document.getElementById(widgetId);
      const captchaApi = window.grecaptcha?.enterprise || window.grecaptcha;
      if (!container || !captchaApi || typeof captchaApi.render !== "function") return;
      if (container.getAttribute("data-rendered") === "true") return;

      captchaApi.render(widgetId, {
        sitekey: recaptchaSiteKey,
        callback: (token) => {
          setRecaptchaToken(token || "");
        },
        "expired-callback": () => {
          setRecaptchaToken("");
        },
      });
      container.setAttribute("data-rendered", "true");
    };

    const timer = window.setInterval(() => {
      const captchaApi = window.grecaptcha?.enterprise || window.grecaptcha;
      if (captchaApi && typeof captchaApi.render === "function") {
        renderCaptcha();
        window.clearInterval(timer);
      }
    }, 180);

    return () => window.clearInterval(timer);
  }, [recaptchaOpen, recaptchaSiteKey]);

  const processLoginInitiate = async () => {
    setError("");
    setLoading(true);
    const url = `${appSettings.baseUrl}${
      appSettings.endpoints.loginInitiate
    }?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;

    try {
      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("Login failed");

      const data = await response.json();
      setPhoneNo(data?.phoneNo || "");
      setOtpStep(true);
      setOtp("");
    } catch (requestError) {
      console.error("Authentication error:", requestError);
      setError("Sorry login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (otpStep) {
      const isOtpValid = otp.trim() !== "";
      setOtpError(!isOtpValid);
      if (!isOtpValid) return;

      setError("");
      setLoading(true);

      const verifyUrl = `${appSettings.baseUrl}${
        appSettings.endpoints.getTokenByOtp
      }?otp=${encodeURIComponent(otp)}&phoneNo=${encodeURIComponent(phoneNo)}`;

      try {
        const response = await fetch(verifyUrl, { method: "POST" });
        if (!response.ok) throw new Error("OTP validation failed");

        const data = await response.json();
        localStorage.setItem("token", data.token);
        localStorage.setItem("refreshToken", data.refreshToken);
        const isTocAccepted = data?.isTocAccepted === true;
        localStorage.setItem("isTocAccepted", isTocAccepted ? "true" : "false");
        navigate(isTocAccepted ? "/dashboard" : "/authentication/terms-and-conditions");
      } catch (verifyError) {
        console.error("OTP validation error:", verifyError);
        setError("Sorry OTP validation failed");
      } finally {
        setLoading(false);
      }

      return;
    }

    const isEmailValid = email.trim() !== "";
    const isPasswordValid = password.trim() !== "";

    setEmailError(!isEmailValid);
    setPasswordError(!isPasswordValid);

    if (!isEmailValid || !isPasswordValid) return;

    if (!recaptchaToken) {
      if (!recaptchaSiteKey) {
        setError("reCAPTCHA site key is not configured in appsettings.json.");
        return;
      }
      setPendingLoginRequest(true);
      setRecaptchaOpen(true);
      return;
    }

    await processLoginInitiate();
  };

  const handleRecaptchaContinue = async () => {
    if (!pendingLoginRequest || !recaptchaToken) return;
    setRecaptchaOpen(false);
    setPendingLoginRequest(false);
    await processLoginInitiate();
  };

  return (
    <BasicLayout image="" wide>
      <Card
        sx={{
          width: "100%",
          maxWidth: 980,
          mx: "auto",
          borderRadius: 6,
          overflow: "hidden",
          boxShadow: "0 16px 30px rgba(2,6,23,0.18)",
        }}
      >
        <Grid container>
          <Grid item xs={12} md={4.5}>
            <MDBox
              sx={{
                bgcolor: "#55575b",
                color: "#ffffff",
                px: { xs: 3, md: 4 },
                py: { xs: 3, md: 4.5 },
                height: "100%",
                borderBottomRightRadius: { xs: 0, md: 64 },
              }}
            >
              <MDTypography variant="h2" fontWeight="bold" color="white">
                Log In
              </MDTypography>
            </MDBox>
          </Grid>

          <Grid item xs={12} md={7.5}>
            <MDBox px={{ xs: 2.5, md: 5 }} py={{ xs: 2.8, md: 4 }}>
              <MDBox component="form" role="form">
                <MDBox mb={2}>
                  <MDInput
                    type="email"
                    label="Email"
                    fullWidth
                    value={email}
                    error={emailError}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (e.target.value.trim()) setEmailError(false);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    fullWidth
                    value={password}
                    error={passwordError}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (e.target.value.trim()) setPasswordError(false);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClickShowPassword} edge="end">
                            {showPassword ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
                {otpStep && (
                  <>
                    <MDBox mb={2}>
                      <MDInput
                        type="text"
                        label="OTP"
                        fullWidth
                        value={otp}
                        error={otpError}
                        onChange={(e) => {
                          setOtp(e.target.value);
                          if (e.target.value.trim()) setOtpError(false);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneAndroidIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </MDBox>
                    <MDBox mt={-1} mb={2}>
                      <MDTypography variant="caption" color="warning" fontWeight="regular">
                        OTP is valid for 1 minute.
                      </MDTypography>
                    </MDBox>
                  </>
                )}
                <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                  <MDTypography
                    component={Link}
                    to="/authentication/forgot-password"
                    variant="button"
                    color="primary"
                    fontWeight="medium"
                    textGradient
                  >
                    Forgot password?
                  </MDTypography>
                </MDBox>
                {error && (
                  <MDBox mt={2} textAlign="center">
                    <MDTypography variant="button" color="error" fontWeight="medium">
                      {error}
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox mt={3} mb={1} display="flex" justifyContent="center">
                  <MDButton
                    variant="gradient"
                    color="error"
                    sx={{ minWidth: 220, borderRadius: 4 }}
                    onClick={handleSignIn}
                    disabled={loading}
                  >
                    {loading ? <AppLoader size={20} /> : otpStep ? "verify otp" : "send otp"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      <Dialog open={recaptchaOpen} onClose={() => setRecaptchaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Security Verification</DialogTitle>
        <DialogContent>
          <MDTypography sx={{ mb: 2, color: "#475569" }}>
            Please complete the security check to continue.
          </MDTypography>
          {recaptchaSiteKey ? (
            <MDBox
              id="admin-signin-recaptcha"
              sx={{
                minHeight: 78,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          ) : (
            <MDTypography sx={{ color: "#dc2626", fontSize: "0.9rem" }}>
              Missing reCAPTCHA site key in appsettings.json (security.recaptchaSiteKey).
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setRecaptchaOpen(false);
              setPendingLoginRequest(false);
            }}
            sx={{
              minWidth: 104,
              borderColor: "#d32f2f",
              color: "#b71c2a",
              fontWeight: 700,
              "&:hover": {
                borderColor: "#b71c2a",
                bgcolor: "#fff1f2",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!recaptchaToken}
            onClick={handleRecaptchaContinue}
            sx={{
              minWidth: 116,
              bgcolor: "#b71c2a",
              color: "#ffffff !important",
              fontWeight: 700,
              "&:hover": { bgcolor: "#991b1b" },
              "&.Mui-disabled": {
                bgcolor: "#d1d5db",
                color: "#6b7280 !important",
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </BasicLayout>
  );
}

export default Basic;
